import {
  type InitAvo,
  normalizeAnalyticsEnvironment,
  registerAnalytics,
  updatePageContext,
} from '@susu/analytics-client';
import { ApplicationId, AvoEnv, initAvo as initAvoFromLib, PageType, setSystemProperties } from '@susu/custom-made/Avo';

import { Clients, ClientType } from '../utils/constants';
import { typeEnvironments } from '../utils/interfaces';

let isSegmentInitialized = false;

/**
 * Bridges the shared client's generic contract onto this source's generated
 * client. It used to be called directly, after initializeAnalytics returned,
 * which left Custom Made out of the application registry entirely: no GA
 * identity, no platform cookies, no common attribution properties, and a
 * hardcoded version on every event.
 */
const initAvo: InitAvo = (options, systemProperties, destinationOptions, destination) =>
  initAvoFromLib(
    { env: options.env === 'prod' ? AvoEnv.Prod : AvoEnv.Dev, webDebugger: false },
    systemProperties as unknown as Parameters<typeof initAvoFromLib>[1],
    destinationOptions,
    destination as Parameters<typeof initAvoFromLib>[3],
  );

type CustomMadePage = {
  client: ClientType;
  language: string;
  countryCode: string;
  /** The in-store customer's account number. */
  accountNr?: string;
};

/**
 * What Custom Made, as page owner, tells the shared client about the page: the
 * locale, which the standalone /configurator path carries none of, in store
 * the customer whose profile the advisor works in, for whom there is no
 * customer cookie, and the page type. The client puts them on every event of
 * the page.
 */
const toPageContext = ({ client, language, countryCode, accountNr }: CustomMadePage) => ({
  locale: `${language}-${countryCode}`.toLowerCase(),
  customerId: client === Clients.inStore ? accountNr : undefined,
  pageType: PageType.CUSTOMMADE,
});

export const initializeAnalytics = ({
  environment,
  client,
  version,
  language,
  countryCode,
  accountNr,
}: CustomMadePage & {
  environment: typeEnvironments;
  version: string;
}): void => {
  if (isSegmentInitialized) {
    return;
  }
  isSegmentInitialized = true;

  registerAnalytics({
    surface: {
      // The same `client` flag that picks the Size Passport client also picks
      // the Segment source and the consent posture: the online configurator is
      // a consumer page behind OneTrust, the in-store one runs on a colleague
      // device with no banner.
      placement: client === Clients.onLine ? 'website' : 'inStore',
      environment: normalizeAnalyticsEnvironment(environment),
      ownsPage: true,
      ...toPageContext({ client, language, countryCode, accountNr }),
    },
    applicationId: ApplicationId.CUSTOM_MADE,
    version,
    initAvo,
    // Without this the client has nowhere to push GA identity once GA4's
    // cookies settle, so the three ga* fields stay absent (D3).
    setAvoSystemProperties: setSystemProperties as unknown as (properties: Record<string, unknown>) => void,
  });
};

/**
 * Keeps the page context current when the locale or the in-store customer
 * changes after {@link initializeAnalytics}. A no-op before it. Embedded in
 * another page (the storefront), the host owns the page and its context, and
 * the shared client ignores this.
 */
export const setCustomMadePageContext = (page: CustomMadePage): void => {
  if (!isSegmentInitialized) {
    return;
  }
  updatePageContext(ApplicationId.CUSTOM_MADE, toPageContext(page));
};
